/**
 * DecorationsLayer — overlay tipo Canva sobre cada hoja del plan.
 *
 * Renderiza las `PlanDecoration[]` posicionadas en porcentaje del contenedor
 * (sobrevive cambios de viewport / impresión / link público). En modo edit
 * cada decoración es seleccionable, draggable, resizable, rotable y
 * eliminable; en readonly solo se renderiza.
 *
 * Persiste en `PlanSection.decorations[]` vía `onChange` del editor
 * (autosave normal del plan). Cada interacción dispara onChange completo
 * — barato porque las decoraciones son objetos chicos.
 *
 * Z-index: 5 (por encima del watermark del logo que es z-index 1, debajo
 * del contenido editable que es z-index 2-3).
 */
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { LibraryIcon, getIconEntry } from "../../lib/IconLibrary";
import type { PlanDecoration } from "../../types/plan";

interface DecorationsLayerProps {
  decorations: PlanDecoration[];
  /** En readonly solo se renderiza (vista pública, PDF). */
  readonly?: boolean;
  /** Llamado cuando cambia el array completo (drag, resize, delete, etc.). */
  onChange?: (next: PlanDecoration[]) => void;
  /** Color default para íconos que no tienen color propio. Si no se pasa,
   *  usa currentColor (típicamente el accent del plan). */
  accentColor?: string;
}

type DragKind = "move" | "resize" | "rotate";

interface DragState {
  id: string;
  kind: DragKind;
  /** Para "move": offset entre el centro de la deco y el pointer al iniciar
   *  el drag, en % del contenedor. Para "resize"/"rotate": valor inicial. */
  startX: number;
  startY: number;
  startSize: number;
  startRotation: number;
  /** Bounding rect del `.al-page` contenedor — para convertir px del mouse a %. */
  rect: DOMRect;
}

export function DecorationsLayer({
  decorations,
  readonly,
  onChange,
  accentColor,
}: DecorationsLayerProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const layerRef = useRef<HTMLDivElement | null>(null);

  // ESC = deselect; DEL = eliminar selected
  useEffect(() => {
    if (readonly) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSelectedId(null);
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId && onChange) {
        // Solo eliminar si el foco NO está en un input editable
        const ae = document.activeElement;
        if (ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" || (ae as HTMLElement).isContentEditable)) {
          return;
        }
        e.preventDefault();
        onChange(decorations.filter((d) => d.id !== selectedId));
        setSelectedId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [readonly, selectedId, decorations, onChange]);

  // Click fuera de cualquier deco = deselect
  useEffect(() => {
    if (readonly || !selectedId) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!layerRef.current) return;
      // Buscar si el click fue en una deco o en sus controles
      const decoEl = (e.target as HTMLElement).closest("[data-deco-id], [data-deco-handle]");
      if (!decoEl && !layerRef.current.contains(target)) return;
      if (!decoEl) {
        // Click dentro del layer pero fuera de una deco específica → deselect
        // (no se dispara porque el layer tiene pointer-events: none excepto
        // sobre las decos; este branch sería para clicks sobre las decos pero
        // no en ningún handle)
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [readonly, selectedId]);

  // Pointer-move global mientras hay drag activo
  useEffect(() => {
    if (!drag || readonly || !onChange) return;
    const onMove = (e: PointerEvent) => {
      const xPct = ((e.clientX - drag.rect.left) / drag.rect.width) * 100;
      const yPct = ((e.clientY - drag.rect.top) / drag.rect.height) * 100;
      onChange(
        decorations.map((d) => {
          if (d.id !== drag.id) return d;
          if (drag.kind === "move") {
            return { ...d, x: clamp(xPct, 0, 100), y: clamp(yPct, 0, 100) };
          }
          if (drag.kind === "resize") {
            // Distancia desde el centro de la deco hasta el pointer
            // (en px del contenedor) → multiplicada por 2 para diameter.
            const cxPx = (drag.startX / 100) * drag.rect.width;
            const cyPx = (drag.startY / 100) * drag.rect.height;
            const dx = e.clientX - drag.rect.left - cxPx;
            const dy = e.clientY - drag.rect.top - cyPx;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const newSize = clamp(Math.round(dist * 2), 16, 480);
            return { ...d, size: newSize };
          }
          if (drag.kind === "rotate") {
            const cxPx = (drag.startX / 100) * drag.rect.width;
            const cyPx = (drag.startY / 100) * drag.rect.height;
            const dx = e.clientX - drag.rect.left - cxPx;
            const dy = e.clientY - drag.rect.top - cyPx;
            // atan2 devuelve en radianes, -π a π. Convertir a grados con
            // offset de +90 para que "arriba" sea 0°.
            const deg = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
            // Normalizar a -180..180
            const norm = ((deg + 180) % 360) - 180;
            return { ...d, rotation: Math.round(norm) };
          }
          return d;
        }),
      );
    };
    const onUp = () => setDrag(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [drag, decorations, onChange, readonly]);

  const startDrag = (
    e: ReactPointerEvent,
    deco: PlanDecoration,
    kind: DragKind,
  ) => {
    if (readonly || !onChange) return;
    e.preventDefault();
    e.stopPropagation();
    // Buscar el contenedor `.al-page` — el layer está adentro, así que el
    // parent del layer es el page. closest() resuelve robusto.
    const layer = layerRef.current;
    const pageEl = layer?.closest(".al-page") as HTMLElement | null;
    if (!pageEl) return;
    const rect = pageEl.getBoundingClientRect();
    setSelectedId(deco.id);
    setDrag({
      id: deco.id,
      kind,
      startX: deco.x,
      startY: deco.y,
      startSize: deco.size,
      startRotation: deco.rotation ?? 0,
      rect,
    });
  };

  // Detectar si el array está vacío y modo readonly → nada que mostrar.
  if (decorations.length === 0 && readonly) return null;

  return (
    <div
      ref={layerRef}
      className={"al-decorations" + (readonly ? " is-readonly" : "")}
      // pointer-events:none en el layer; las decos individuales activan auto
      style={{ pointerEvents: readonly ? "none" : "none" }}
    >
      {decorations.map((d) => {
        const isSelected = !readonly && selectedId === d.id;
        const color = d.color || accentColor || "currentColor";
        const opacity = d.opacity ?? 1;
        const rotation = d.rotation ?? 0;
        const size = d.size;
        return (
          <div
            key={d.id}
            data-deco-id={d.id}
            className={"al-decoration" + (isSelected ? " is-selected" : "")}
            style={{
              left: `${d.x}%`,
              top: `${d.y}%`,
              width: size,
              height: size,
              transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
              pointerEvents: readonly ? "none" : "auto",
              cursor: readonly ? "default" : (drag?.id === d.id && drag.kind === "move" ? "grabbing" : "grab"),
              opacity,
              color,
            }}
            onPointerDown={(e) => startDrag(e, d, "move")}
            onClick={(e) => {
              if (readonly) return;
              e.stopPropagation();
              setSelectedId(d.id);
            }}
            title={readonly ? undefined : getIconEntry(d.icon)?.label}
          >
            <LibraryIcon id={d.icon} size={size} color={color} strokeWidth={1.6} />

            {/* Handles solo si seleccionado y no readonly */}
            {isSelected && !readonly && (
              <>
                {/* Handle resize en esquina inferior derecha */}
                <div
                  data-deco-handle="resize"
                  className="al-deco-handle al-deco-handle-resize"
                  onPointerDown={(e) => startDrag(e, d, "resize")}
                  title="Arrastrá para cambiar el tamaño"
                />
                {/* Handle rotate arriba */}
                <div
                  data-deco-handle="rotate"
                  className="al-deco-handle al-deco-handle-rotate"
                  onPointerDown={(e) => startDrag(e, d, "rotate")}
                  title="Arrastrá para rotar"
                />
                {/* Botón eliminar arriba-derecha */}
                <button
                  type="button"
                  data-deco-handle="delete"
                  className="al-deco-handle al-deco-handle-delete"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onChange) onChange(decorations.filter((x) => x.id !== d.id));
                    setSelectedId(null);
                  }}
                  title="Eliminar (también Del/Backspace)"
                  aria-label="Eliminar decoración"
                >
                  ×
                </button>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Helper para insertar una decoración nueva en el centro de la sección.
 * Devuelve el array actualizado (no muta). Usar desde el picker:
 *   onChange({ ...section, decorations: insertDecoration(section.decorations, "dumbbell") })
 */
export function insertDecoration(
  current: PlanDecoration[] | undefined,
  icon: string,
): PlanDecoration[] {
  return [
    ...(current || []),
    {
      id: `dec_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      icon,
      x: 50,
      y: 50,
      size: 72,
      rotation: 0,
      opacity: 1,
    },
  ];
}
